import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Database, CheckCircle2, Clock, Users, Zap, BarChart3 } from "lucide-react";
import { cn } from "../lib/utils";
import { StatsSkeleton, CardSkeleton } from "../components/Skeletons";

function StatCard({ label, value, icon: Icon, accent, testId }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-6 transition-shadow duration-200 hover:shadow-sm"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.2em] font-medium text-slate-500">{label}</p>
        <div className={cn("h-7 w-7 rounded-md flex items-center justify-center", accent)}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
        </div>
      </div>
      <p className="mt-6 text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
        {value?.toLocaleString?.("pt-BR") ?? value ?? 0}
      </p>
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/logs?limit=8&page=1"),
      ]);
      setStats(s.data);
      setLogs(l.data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 15000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="space-y-8" data-testid="overview-page">
        <StatsSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-6">
        <StatCard testId="stat-total" label="Total" value={stats?.total_accounts} icon={Database} accent="bg-slate-100 text-slate-700" />
        <StatCard testId="stat-available" label="Disponíveis" value={stats?.available} icon={CheckCircle2} accent="bg-emerald-50 text-emerald-700" />
        <StatCard testId="stat-reserved" label="Reservadas" value={stats?.reserved} icon={Clock} accent="bg-amber-50 text-amber-700" />
        <StatCard testId="stat-suspended" label="Suspensas" value={stats?.suspended} icon={Clock} accent="bg-red-50 text-red-700" />
        <StatCard testId="stat-users" label="Usuários" value={stats?.registered_users} icon={Users} accent="bg-slate-100 text-slate-700" />
        <StatCard testId="stat-requests-today" label="Hoje" value={stats?.requests_today} icon={Zap} accent="bg-slate-900 text-white" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-5 sm:px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-semibold tracking-tight text-slate-900">Atividade Recente</h2>
            <p className="text-xs text-slate-500 mt-0.5">Últimos consumos de conta pelos usuários do app</p>
          </div>
          <BarChart3 className="h-4 w-4 text-slate-400" />
        </div>
        {logs.length === 0 ? (
          <div className="p-6 sm:p-8 text-sm text-slate-500" data-testid="activity-empty">
            Nenhum consumo registrado ainda.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-left">
                  <th className="px-5 sm:px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500">Usuário</th>
                  <th className="px-5 sm:px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500">Conta Consumida</th>
                  <th className="px-5 sm:px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500">Quando</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-slate-200">
                    <td className="px-5 sm:px-6 py-3 text-slate-700">{l.user_email}</td>
                    <td className="px-5 sm:px-6 py-3 text-slate-700 mono text-xs">{l.account_email}</td>
                    <td className="px-5 sm:px-6 py-3 text-slate-500 text-xs">{new Date(l.timestamp).toLocaleString("pt-BR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
